import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { returnsAPI } from '../../services/api';
import { useNotification } from '../../hooks/useNotification';

const ReturnsList = () => {
  const { t } = useTranslation();
  const { showSuccess, showError, showWarning } = useNotification();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [returns, setReturns] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadReturns();
  }, [statusFilter]);

  const loadReturns = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const response = await returnsAPI.getAll(params);
      setReturns(response.data.data || []);
    } catch (error) {
      console.error('Error loading returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('common.confirm'))) {
      try {
        await returnsAPI.delete(id);
        loadReturns();
      } catch (error) {
        showError(error.response?.data?.message || t('messages.error'));
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', badge: 'bg-yellow-100' },
      'Approved': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100' },
      'Processed': { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', badge: 'bg-emerald-100' },
      'Completed': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', badge: 'bg-green-100' },
      'Cancelled': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100' },
    };
    return colors[status] || colors['Pending'];
  };

  const filteredReturns = returns.filter(ret =>
    ret.return_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ret.sales_order?.order_number?.toString().includes(searchTerm) ||
    ret.sales_order?.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
              {t('returns.title')}
            </h1>
            <Link
              to="/sales-orders"
              className="text-sm text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('common.back')}
            </Link>
          </div>
          <p className="text-gray-600">{t('returns.subtitle')}</p>
        </div>

        {/* Filters & Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.search')}
              </label>
              <div className="relative">
                <svg className="absolute left-3 top-3 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('returns.searchPlaceholder') || 'Search returns...'}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('common.status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
              >
                <option value="">{t('common.all')}</option>
                <option value="Pending">{t('returns.statusPending')}</option>
                <option value="Approved">{t('returns.statusApproved')}</option>
                <option value="Processed">{t('returns.statusProcessed')}</option>
                <option value="Completed">{t('returns.statusCompleted')}</option>
                <option value="Cancelled">{t('returns.statusCancelled')}</option>
              </select>
            </div>

            {/* Actions */}
            <div className="flex items-end gap-2">
              <button
                onClick={loadReturns}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-gray-600 to-gray-700 text-white rounded-lg hover:shadow-md transition-all font-medium flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {t('common.refresh')}
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {!loading && (
          <div className="mb-4 text-sm text-gray-600">
            {t('common.showing')} <span className="font-semibold text-gray-900">{filteredReturns.length}</span> {t('common.of')} <span className="font-semibold text-gray-900">{returns.length}</span> {t('returns.title').toLowerCase()}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : filteredReturns.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-16 text-center">
            <div className="text-gray-400 mb-2">
              <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-gray-500">{t('common.noData')}</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredReturns.map(ret => (
              <div
                key={ret.id}
                className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:border-red-200 transition-all duration-300 overflow-hidden group"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    {/* Main Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900">
                            #{ret.return_number}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {new Date(ret.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <span className={`px-4 py-1.5 text-xs font-bold rounded-full whitespace-nowrap ${getStatusColor(ret.status).badge} ${getStatusColor(ret.status).text}`}>
                          {ret.status}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
                        <div className="border-l-2 border-gray-200 pl-4">
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                            {t('salesOrders.orderNumber')}
                          </p>
                          <p className="text-sm font-bold text-gray-900">
                            #{ret.sales_order?.order_number}
                          </p>
                        </div>
                        <div className="border-l-2 border-gray-200 pl-4">
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                            {t('salesOrders.customer')}
                          </p>
                          <p className="text-sm font-semibold text-gray-900">
                            {ret.sales_order?.customer?.name}
                          </p>
                        </div>
                        <div className="border-l-2 border-gray-200 pl-4">
                          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-1">
                            {t('returns.reason')}
                          </p>
                          <p className="text-sm text-gray-700 font-medium">
                            {ret.return_reason}
                          </p>
                        </div>
                        <div className="border-l-2 border-red-200 pl-4 bg-red-50 p-3 rounded-lg">
                          <p className="text-xs font-medium text-red-600 uppercase tracking-wide mb-1">
                            {t('returns.totalRefund')}
                          </p>
                          <p className="text-lg font-bold text-red-600">
                            {t('common.currency')} {parseFloat(ret.total_refund).toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {ret.notes && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-2">
                          <p className="text-xs text-gray-600 font-medium mb-1">{t('common.notes')}:</p>
                          <p className="text-sm text-gray-700">
                            {ret.notes}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Link
                        to={`/returns/${ret.id}`}
                        className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                        title={t('common.edit')}
                      >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21H3v-3.5L16.732 3.732z" />
                        </svg>
                      </Link>
                      {ret.status === 'Pending' && (
                        <button
                          onClick={() => handleDelete(ret.id)}
                          className="p-2.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200"
                          title={t('common.delete')}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReturnsList;
